// @flow
import { View } from "extensions/marionette";

import { whitelistedPivots } from "common/helpers/dimensionHelpers";
import { FilterBarRedux } from "common/components/filterBar/FilterBar";
import { store } from "common/store";

import mainTemplate from "./widgetSettings.jst";
import optionsTemplate from "./widgetOptions.jst";

import "./widgetSettings.scss";

// type DropDownItemType = { name: string, value: string, selected?: boolean };

const pivotLabels = {
    bar: "Bars",
    pie: "Pie slices",
    table: "Table rows"
};

const ChartOptions = View.extend({
    template: optionsTemplate,
    
    ui: {
        sampleChart: ".sample-chart"
    },
    
    events: {
        "focus .charts-dimension,.tabs-dimension": "changeSampleClass",
        "blur .charts-dimension,.tabs-dimension": "setSampleInactive",
        "click .choice input": "setByCountOrAmount"
    },
    
    regions: {
        tabsDD: ".tabs-dimension",
        chartsDD: ".charts-dimension"
    },
    templateContext() {
        const widgetType = this.model.get("widgetType");
        const pivotLabel = widgetType === "doublePivot" ? pivotLabels[this.model.get("options").bodyType] : pivotLabels[widgetType];
        const widgetData = this.model.subtypeModel.get(widgetType);
        const defaultMetric = (widgetData.defaultMetric || (widgetData.data && widgetData.data.defaultMetric)) || "";
        const bodyType = this.model.get("options").bodyType || widgetData.bodyType;
        const activeType = widgetType === "doublePivot" ? bodyType : widgetType;
        
        return {
            multiPivot: this.isMultiPivot(),
            pivotClass: this.getPivotClass(),
            activeType,
            hideActiveType: this.hideMetric(this.model),
            pivotLabel,
            byAmount: this.model.get("options").by === "amount" || defaultMetric === "amount" ? "checked" : "",
            byCount: this.model.get("options").by === "count" || defaultMetric === "count" ? "checked" : ""
        };
    },
    
    hideMetric(model) {
        // hide count/amount from widgets where inappropriate or logic not build yet.
        if ((model.get("widgetType") === "map") || (model.get("widgetType") === "trendline")) {
            return "hidden";
        }
        if (model.get("widgetSubtype") === "chargebacks") {
            return "hidden";
        }
        
        if ((model.get("widgetSubtype") === "fees") && (model.get("widgetType") === "quick")) {
            return "hidden";
        }
        // hiding double pivot in auths (declines by provider reason code)
        // for now as couldn't get it to refresh properly when started as count and switched to amount.
        if ((model.get("widgetType") === "doublePivot") && model.get("widgetSubtype") === "authorizations") {
            return "hidden";
        }
        return "";
    },
    
    clearSampleClass() {
        this.ui.sampleChart.removeClass("inactive tabs chart");
    },
    
    setByCountOrAmount(e) {
        const val = e.currentTarget.value;
        const currentOptions = this.model.get("options");
        this.model.set("options", Object.assign(currentOptions, { by: val }), { silent: true });
    },
    
    setSampleInactive() {
        this.clearSampleClass();
        this.ui.sampleChart.addClass("inactive");
    },
    
    changeSampleClass(e) {
        const showSample = e.currentTarget.dataset.sample;
        this.clearSampleClass();
        this.ui.sampleChart.addClass(showSample);
    },
    
    isMultiPivot() {
        const options = this.model.get("options");
        return options.pivot && options.pivot.length > 1;
    },
    
    getPivotClass() {
        const multiPivot = this.isMultiPivot();
        return multiPivot ? "multi-pivot" : "single-pivot";
    }
});

export const SettingsView = View.extend({
    template: mainTemplate,
    
    regions: {
        filters: ".widget-filters",
        options: ".widget-chart-options"
    },
    ui: {
        typeIcon: ".type-icons"
    },
    
    events: {
        "change #title": "changeTitle",
        // "click .type-icons i": "changeChartType",  // disable for now
        "click div.save": "saveWidget"
    },
    
    forceUpdateOnSave: false,
    
    childViewEvents: {
        updateFilters: "updateFilters"
    },
    
    initialize() {
        this.localData = this.model.clone();
        this.showFilters();
    },
    
    updateFilters(f) {
        this.localData.set("qualifiers", f.toString());
        this.forceUpdateOnSave = true;
    },
    
    mapWidgetSubTypeToApiKey(subtype) {
        if (subtype === "sales" || subtype === "refunds") {
            return "settlements";
        }
        return subtype;
    },
    
    showFilters() {
        const apiKey = this.mapWidgetSubTypeToApiKey(this.model.get("widgetSubtype"));
        const dims = [
            ...Object.keys(store.getState().common.merchant_dimensions.data),
            ...whitelistedPivots[apiKey]
        ].sort();
        
        const bar = (
            <FilterBarRedux
                container=".content"
                sources={dims}
                categoryModeAvailable={false}
                qualifiers={this.localData.get("qualifiers") || ""}
                onChange={(qualifiers) => {
                    this.updateFilters(qualifiers);
                }}
            />
        );
        this.showChildView("filters", bar);
    },
    
    templateContext(): Object {
        const widgetType = this.model.get("widgetType");
        const chartType = widgetType === "doublePivot" ? this.model.get("options").bodyType : widgetType;
        return {
            chartType
        };
    },
    
    changeTitle(e) {
        const title = e.currentTarget.value;
        this.localData.set({ title, userEditedTitle: true }, { silent: true });
    },
    
    changeChartType(e) {
        const whichChart = e.currentTarget.dataset.chart;
        this.ui.typeIcon[0].className = `type-icons ${whichChart}`;
        this.model.changeChartType(whichChart);
        this.renderOptions();
    },
    
    changeChartTypeDblPivot(chart) {
        const currentOptions = this.model.get("options");
        this.set("options", Object.assign(currentOptions, { bodyType: chart }));
    },
    
    onRender() {
        this.renderOptions();
    },
    
    renderOptions() {
        const optionsView = new ChartOptions({ model: this.localData });
        this.showChildView("options", optionsView);
    },
    
    saveWidget() {
        const updatedModel = this.localData.clone();
        
        this.model.set(updatedModel.attributes);
        this.model.subtypeModel = updatedModel.subtypeModel;
        this.model.collection.save();
        this.getOption("srcWidget").refreshWidgetOnEdit(this.forceUpdateOnSave);
        this.parent.dismiss();
    }
});
